// Libraries
import React, { PureComponent } from 'react'
import PropTypes from 'helpers/proptypes'
import { connect } from 'react-redux'
import moment from 'moment'
import { withTranslation } from 'react-i18next'
// Components
import DatePicker from 'react-datepicker'
import { Form, Button } from 'semantic-ui-react'
import { InputExpandingOnFocus } from 'layouts'
import { SearchInput, SelectInput } from 'components/inputs'
import { CategoriesDropdown } from 'components/forms/categories/CategoriesDropdown'
// Selectors
import { getLocation } from 'redux/reducers/router'
// Redux
import { getOrganizationsList } from 'redux/entities/selectors'
import { fetchOrganizations } from 'redux/entities/actions'
// Utils
import { getTranslatedOptions } from 'helpers/options'
import { bookingsFollowupsActions } from 'helpers/followups'
import './RequestSearchForm.css'

const mapStateToProps = (state) => ({
  location: getLocation(state),
  organizations: getOrganizationsList(state),
})

const mapDispatchToProps = (dispatch) => ({
  loadOrganizations: () => dispatch(fetchOrganizations({ query: { all: true } })),
})

const getBookingsOptions = (t) => [
  { text: t('Requests::BookingOptions::All'), value: '' },
  { text: t('Requests::BookingOptions::Has bookings'), value: 'true' },
  { text: t('Requests::BookingOptions::No bookings'), value: 'false' },
]

const getDateOptions = (t) => [
  { text: t('Requests::DateOptions::All'), value: '' },
  { text: t('Requests::DateOptions::Request a date'), value: 'true' },
  { text: t('Requests::DateOptions::Request information'), value: 'false' },
]

const getStatusOptions = (t) => [
  { text: t('Requests::StatusOptions::All'), value: '' },
  { text: t('Requests::StatusOptions::Pending'), value: 'pending' },
  { text: t('Requests::StatusOptions::Approved'), value: 'approved' },
  { text: t('Requests::StatusOptions::Canceled'), value: 'canceled' },
  { text: t('Requests::StatusOptions::Expired'), value: 'expired' },
]

const getPrivatizedOptions = (t) => [
  { text: t('Requests::PrivatizedOptions::All'), value: '' },
  { text: t('Requests::PrivatizedOptions::Privatized'), value: 'true' },
  { text: t('Requests::PrivatizedOptions::Public'), value: 'false' },
]

const getDateRangeOptions = (t) => [
  { text: t('Requests::DateRangeOptions::approved'), value: 'approved_at' },
  { text: t('Requests::DateRangeOptions::created'), value: 'created_at' },
  { text: t('Requests::DateRangeOptions::canceled'), value: 'canceled_at' },
  { text: t('Requests::DateRangeOptions::date'), value: 'date' },
  { text: t('Requests::DateRangeOptions::expired'), value: 'expired_at' },
  { text: t('Requests::DateRangeOptions::updated'), value: 'updated_at' },
  { text: t('Requests::DateRangeOptions::will expire'), value: 'will_expire_at' },
]

class _RequestSearchForm extends PureComponent {
  static propTypes = {
    t: PropTypes.func.isRequired,
    location: PropTypes.immutable.map.isRequired,
    organizations: PropTypes.immutable.list,
    loadOrganizations: PropTypes.func.isRequired,
  }

  static defaultProps = {
    organizations: undefined,
  }

  static contextTypes = { router: PropTypes.object }

  tagsOpts = []
  UNSAFE_componentWillMount() {
    this.props.loadOrganizations()
    return this.updateStateFromLocation(this.props)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.location.equals(this.props.location)) return this.updateStateFromLocation(nextProps)
  }

  updateStateFromLocation(props) {
    return this.setState({
      search: props.location.getIn(['query', 'search'], ''),
      guest: props.location.getIn(['query', 'guest'], ''),
      host: props.location.getIn(['query', 'host'], ''),
      event: props.location.getIn(['query', 'event'], ''),
      location: props.location.getIn(['query', 'location'], ''),
      tags: props.location.getIn(['query', 'tags'], []),
      privatized: props.location.getIn(['query', 'privatized'], ''),
      followups: props.location.getIn(['query', 'followups'], ''),
      organization_ids: props.location.getIn(['query', 'organization_ids'], ''),
      organization_tags: props.location.getIn(['query', 'organization_tags'], []),
      occasion: props.location.getIn(['query', 'occasion'], ''),
      has_bookings: props.location.getIn(['query', 'has_bookings'], ''),
      has_date: props.location.getIn(['query', 'has_date'], ''),
      minGuests: props.location.getIn(['query', 'minGuests'], ''),
      maxGuests: props.location.getIn(['query', 'maxGuests'], ''),
    })
  }

  updateQuery(query = this.state) {
    return this.context.router.push(this.props.location.mergeIn(['query'], { ...query, offset: 0 }).toJS())
  }

  clearSorting = () => {
    return this.context.router.push(this.props.location.mergeIn(['query'], { sortBy: '', order: '' }).toJS())
  }

  clearFilters = () => {
    return this.setState(
      {
        search: '',
        guest: '',
        host: '',
        event: '',
        location: '',
        daterange: '',
        fromdate: '',
        todate: '',
        tags: [],
        userId: '',
        followups: '',
        organizations: '',
        organization_tags: [],
        occasion: '',
        has_bookings: false,
        has_date: '',
        minGuests: '',
        maxGuests: '',
      },
      () => this.updateQuery({ ...this.state, status: '' }),
    )
  }

  updateDateRange = (value) => {
    this.setState({ daterange: value })
    if (value === '') {
      this.setState({ fromdate: '' })
      this.setState({ todate: '' })
    }
  }

  updateFromDate = (date) => {
    const fromdate = moment(date).format('YYYY-MM-DD')
    if (!this.state.daterange) this.setState({ daterange: 'date' })
    this.setState({ fromdate })
    if (!this.state.todate || this.state.todate < fromdate) {
      this.setState({ todate: fromdate })
    }
  }

  updateToDate = (date) => {
    const todate = moment(date).format('YYYY-MM-DD')
    if (!this.state.daterange) this.setState({ daterange: 'date' })
    this.setState({ todate })
    if (!this.state.fromdate || this.state.fromdate > todate) {
      this.setState({ fromdate: todate })
    }
  }

  handleSubmit = (e) => {
    e.preventDefault()
    return this.updateQuery()
  }

  formatOrganizationsOptions = () =>
    this.props.organizations.map((org) => ({ text: org.name, value: org.id.toString() }))

  render() {
    const { t } = this.props

    return (
      <Form onSubmit={this.handleSubmit} autoComplete='off'>
        <Form.Group className='__bookingFormGroup'>
          <InputExpandingOnFocus
            label={t('Requests::Request')}
            Component={SearchInput}
            queryName='search'
            placeholder={t('Requests::id')}
            icon='search'
            iconPosition='left'
            value={this.state.search}
            onChange={(value) => this.setState({ search: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Has bookings')}
            blurWidth={80}
            Component={SelectInput}
            queryName='has_bookings'
            options={getBookingsOptions(t)}
            fluid
            search
          />

          <InputExpandingOnFocus
            label={t('Requests::Request type')}
            blurWidth={80}
            Component={SelectInput}
            queryName='has_date'
            options={getDateOptions(t)}
            fluid
            search
          />

          <InputExpandingOnFocus
            label={t('Requests::Host::Host')}
            focusWidth={220}
            Component={SearchInput}
            queryName='host'
            placeholder={t('Requests::Host::id, first/last name, email')}
            icon='search'
            iconPosition='left'
            value={this.state.host}
            onChange={(value) => this.setState({ host: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Guest::Guest')}
            focusWidth={220}
            Component={SearchInput}
            queryName='guest'
            placeholder={t('Requests::Guest::id, first/last name, email')}
            icon='search'
            iconPosition='left'
            value={this.state.guest}
            onChange={(value) => this.setState({ guest: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Experience::Experience')}
            Component={SearchInput}
            queryName='event'
            icon='search'
            iconPosition='left'
            placeholder={t('Requests::Experience::id, title or event date')}
            value={this.state.event}
            onChange={(value) => this.setState({ event: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Location::Location')}
            focusWidth={220}
            Component={SearchInput}
            queryName='location'
            placeholder={t('Requests::Location::Address, city, country (iso), postal code')}
            icon='search'
            iconPosition='left'
            value={this.state.location}
            onChange={(value) => this.setState({ location: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Occasion')}
            focusWidth={220}
            Component={SearchInput}
            queryName='occasion'
            placeholder={t('Requests::Occasion')}
            icon='search'
            iconPosition='left'
            value={this.state.occasion}
            onChange={(value) => this.setState({ occasion: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Request status')}
            blurWidth={90}
            Component={SelectInput}
            queryName='status'
            options={getStatusOptions(t)}
            fluid
            search
            value={this.state.status}
          />

          <InputExpandingOnFocus
            label={t('Requests::Privatized')}
            blurWidth={80}
            Component={SelectInput}
            queryName='privatized'
            options={getPrivatizedOptions(t)}
            fluid
            search
          />

          <Form.Field>
            <label>{t('Requests::Experience Tags')}</label>
            <CategoriesDropdown
              handleChange={(_, { value: tags }) => this.setState({ tags })}
              value={this.state.tags}
            />
          </Form.Field>

          <Form.Field>
            <label>&nbsp;</label>
            <Button type='submit'>{t('Requests::Search')}</Button>
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <InputExpandingOnFocus
            label={t('Requests::Organizations')}
            blurWidth={120}
            Component={SelectInput}
            queryName='organization_ids'
            options={this.formatOrganizationsOptions().toArray()}
            multiple
            fluid
            search
          />
          <Form.Field>
            <label>{t('Requests::Organization Tags')}</label>
            <CategoriesDropdown
              handleChange={(_, { value }) => this.setState({ organization_tags: value })}
              value={this.state.organization_tags}
            />
          </Form.Field>
          <Form.Field width={3}>
            <label>{t('Requests::DateRange::Search by date range')}</label>
            <SelectInput
              queryName='daterange'
              options={getDateRangeOptions(t)}
              onChange={this.updateDateRange}
              value={this.state.daterange}
              preventNavigation
            />
          </Form.Field>

          <Form.Field>
            <label>{t('Requests::DateRange::Between')}</label>
            <DatePicker
              name='fromdate'
              todayButton={t('Requests::DateRange::Today')}
              selected={this.state.fromdate && moment(this.state.fromdate).isValid() ? moment(this.state.fromdate) : ''}
              onChange={this.updateFromDate}
              placeholderText={t('Requests::DateRange::start date')}
              dateFormat='DD/MM/YYYY'
            />
          </Form.Field>

          <Form.Field>
            <label>{t('Requests::DateRange::And')}</label>
            <DatePicker
              name='todate'
              todayButton={t('Requests::DateRange::Today')}
              selected={this.state.todate && moment(this.state.todate).isValid() ? moment(this.state.todate) : ''}
              onChange={this.updateToDate}
              placeholderText={t('Requests::DateRange::end date')}
              dateFormat='DD/MM/YYYY'
            />
          </Form.Field>

          <InputExpandingOnFocus
            label={t('Requests::Min Guests')}
            blurWidth={80}
            Component={SearchInput}
            queryName='minGuests'
            placeholder={t('Requests::Min Guests')}
            type='number'
            fluid
            value={this.state.minGuests}
            onChange={(value) => this.setState({ minGuests: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Max Guests')}
            blurWidth={80}
            Component={SearchInput}
            type='number'
            queryName='maxGuests'
            placeholder={t('Requests::Max Guests')}
            fluid
            value={this.state.maxGuests}
            onChange={(value) => this.setState({ maxGuests: value })}
          />

          <InputExpandingOnFocus
            label={t('Requests::Followups')}
            blurWidth={80}
            Component={SelectInput}
            queryName='followups'
            options={getTranslatedOptions(t, bookingsFollowupsActions)}
            fluid
            search
          />

          <Form.Field>
            <label>&nbsp;</label>
            <Button type='submit'>{t('Requests::Search')}</Button>
          </Form.Field>

          <Button.Group vertical basic compact size='mini'>
            <Button onClick={this.clearFilters}>{t('Requests::Clear filters')}</Button>
            <Button onClick={this.clearSorting}>{t('Requests::Clear sorting')}</Button>
          </Button.Group>
        </Form.Group>
      </Form>
    )
  }
}

export const RequestSearchForm = withTranslation('common')(
  connect(mapStateToProps, mapDispatchToProps)(_RequestSearchForm),
)
